import { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useNavigationOrderAction,
  useNavigationOrdersList,
  type NavigationOrderListItem,
  type NavigationOrderStatus,
} from './hooks/useNavigationOrders';

const STATUS_LABEL: Record<NavigationOrderStatus, string> = {
  received: 'Sin pagar',
  paid: 'Pagado',
  validated: 'Validado',
  awaiting_review: 'Pendiente',
  in_progress: 'En REC',
  delivered: 'Entregado',
  failed: 'Fallido',
  refunded: 'Reembolsado',
  cancelled: 'Cancelado',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function RecForm({ order }: { order: NavigationOrderListItem }) {
  const action = useNavigationOrderAction();
  const [number, setNumber] = useState(order.recRegistryNumber || '');
  const canPresent = order.status === 'awaiting_review' || order.status === 'in_progress';
  const canResolve = order.status === 'in_progress' || order.status === 'awaiting_review';

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="text-xs text-muted-foreground">
        Nº registro REC
        <input
          className="mt-1 block w-56 rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="REGAGE…"
          disabled={!canPresent}
        />
      </label>
      <button
        type="button"
        disabled={!canPresent || number.trim().length < 6 || action.isPending}
        onClick={() =>
          action.mutate({
            orderId: order.id,
            action: 'present_rec',
            recRegistryNumber: number.trim(),
          })
        }
        className="rounded-md bg-foreground px-3 py-1.5 text-xs font-medium text-background disabled:opacity-40"
      >
        {action.isPending ? 'Guardando…' : 'Marcar presentado'}
      </button>
      <button
        type="button"
        disabled={!canResolve || action.isPending}
        onClick={() => action.mutate({ orderId: order.id, action: 'resolve' })}
        className="rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-40"
      >
        Marcar resuelto
      </button>
      <button
        type="button"
        disabled={action.isPending || order.status === 'failed'}
        onClick={() => action.mutate({ orderId: order.id, action: 'fail', note: 'HITL' })}
        className="rounded-md border border-rose-500/40 px-3 py-1.5 text-xs text-rose-500 disabled:opacity-40"
      >
        Fallido
      </button>
      {action.error ? (
        <p className="basis-full text-xs text-rose-500">
          {action.error instanceof Error ? action.error.message : 'Error'}
        </p>
      ) : null}
    </div>
  );
}

export function NavigationOrdersPanel() {
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const { data, isLoading, refetch, isFetching } = useNavigationOrdersList(status, search);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Declaraciones CHE. El cobro sigue cerrado; cuando haya pedidos, Alberto marca el registro REC aquí.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="all">Todos</option>
          <option value="awaiting_review">Pendientes</option>
          <option value="in_progress">En REC</option>
          <option value="delivered">Entregados</option>
          <option value="failed">Fallidos</option>
        </select>
        <input
          className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          placeholder="Buscar email o masa"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          onClick={() => refetch()}
          className="flex items-center gap-1 text-sm text-muted-foreground"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>
      {isLoading ? (
        <Loader2 className="animate-spin" size={18} />
      ) : !data?.length ? (
        <p className="text-sm text-muted-foreground">No hay declaraciones CHE.</p>
      ) : (
        <ul className="space-y-3">
          {data.map((order) => (
            <li key={order.id} className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex flex-wrap justify-between gap-2 text-sm">
                <strong>{order.publicId}</strong>
                <span className={cn('text-xs')}>{STATUS_LABEL[order.status]}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {order.waterName} · {order.declarationKind} · {order.contactEmail}
              </p>
              <p className="text-xs text-muted-foreground">{fmtDate(order.createdAt)}</p>
              <RecForm order={order} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
